import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button } from './site/ui';
import { useToast } from './admin/overlays';
import { IconArrowLeft, IconCard, IconCheck, IconCheckCircle } from './icons';
import { PLAN_KEYS, SHOW_PAY_PER_CASE, isSelfServePlan, planLabel, planAccent } from './plans';

/** Copy for the situations this modal covers. */
const COPY = {
  gated: {
    heading: 'Continue resolving delivery issues with ParcelBuddy',
    intro:
      "Your free pilot cases have been used. To continue using ParcelBuddy for delivery " +
      "issue resolution, buy a license.",
    subscribeSubtitle: 'Per user, unlimited cases.'
  },
  pilotEnded: {
    heading: 'Your pilot has ended',
    intro:
      "You've used all the free cases included in your pilot. Buy a license to keep taking " +
      'action on delivery issues.',
    subscribeSubtitle: 'Per user, unlimited cases.'
  },
  newMerchant: {
    heading: 'Get started with ParcelBuddy',
    intro:
      "This is your first case. Take it for free right now, and choose how you'd like to " +
      'continue when you\'re ready — a free pilot, or go straight to a license.',
    subscribeSubtitle: 'Start with a free pilot (7 cases), or buy a license for unlimited cases.'
  },
  /**
   * A merchant who already chose pay-per-case, on a case that hasn't been
   * paid for yet. Distinct from `gated` on purpose: re-offering the full
   * "choose how you'd like to proceed" chooser here would ask them to pick a
   * billing model they've already picked — this confirms where they already
   * are and offers exactly two ways forward: pay for this one case, or move
   * to a monthly plan instead.
   */
  payPerCase: {
    heading: "You're on Pay per case",
    intro:
      "You've already chosen pay-per-case billing — this case just hasn't been paid for yet. " +
      'Continue paying per case, or switch to a monthly plan below.',
    subscribeSubtitle: 'Move to a monthly plan instead — your usage so far carries over.'
  },
  /**
   * A subscription merchant who has used their whole cycle's included-case
   * allowance. Same shape as `payPerCase` for the same reason: they've
   * already picked a plan, so this confirms where they are (out of included
   * cases, not out of options) rather than re-offering the first-time
   * chooser.
   */
  quotaFilled: {
    heading: "You've used this cycle's included cases",
    intro:
      "Your plan's case allowance is used up for this billing cycle — this case hasn't been paid " +
      'for. Pay for it at your overage rate, or upgrade to a bigger plan below.',
    subscribeSubtitle: 'More included cases, so this happens less often — your usage carries over.'
  }
};

/**
 * The "continue as you already are" option for the two reasons that have
 * one — payPerCase and quotaFilled. Both hit a per-case "pay to continue"
 * checkout (POST { token }, returns { url }); only the endpoint, price and
 * copy differ. Keeping this as data (not two near-identical code paths) is
 * what let quotaFilled reuse everything payPerCase already built.
 */
const CONTINUE_CONFIG = {
  payPerCase: {
    endpoint: '/api/stripe/next-cycle-checkout',
    label: 'Continue with Pay per case',
    subtitle: 'Pay for this case and keep going exactly as you have been.',
    switchLabel: 'Switch to a monthly plan'
  },
  quotaFilled: {
    endpoint: '/api/stripe/overage-case-checkout',
    label: 'Pay for this case',
    subtitle: "Settle this one case at your plan's overage rate, without changing plans.",
    switchLabel: 'Upgrade to a bigger plan'
  }
};

/**
 * Activation modal — shown for several distinct reasons (`reason` prop),
 * sharing one structure: a single path into the plan ladder (Pilot + the
 * per-user Merchant plan), because the ladder is exactly what the public
 * pricing page shows and this must never disagree with it.
 *
 * `canStartPilot` makes the Pilot card selectable within that ladder and
 * routes a Pilot selection to the free /start-pilot endpoint instead of
 * Stripe checkout — Pilot has no price, so there is nothing to check out.
 *
 * Never dismissable, for any reason: a merchant who has chosen nothing — no
 * pilot, no plan — must choose something before acting on a case, including
 * the first one. The server enforces the same rule
 * (caseAccessService.activationBlock); this is not merely a client-side nudge
 * that a closed modal could bypass.
 */
const ActivationModal = ({
  token,
  caseId,
  onActivated,
  onViewPricing,
  canStartPilot = false,
  reason = 'gated',
  /** Price for this specific case — only meaningful (and passed) for `payPerCase`. */
  nextCycleAmount = null,
  /** Price for this specific case — only meaningful (and passed) for `quotaFilled`. */
  overageAmount = null,
  /**
   * The plan key this merchant is already subscribed to, if any — only
   * meaningful for `payPerCase`/`quotaFilled`, where this modal opens for a
   * merchant who already has a plan (a brand-new merchant choosing their
   * first plan, `gated`/`pilotEnded`/`newMerchant`, has none). Marks that
   * plan's card as current instead of a re-buyable button.
   */
  currentPlan = null
}) => {
  // Pay-per-case is no longer offered (see SHOW_PAY_PER_CASE): a merchant
  // still on it is pointed at the plan grid rather than invited to carry on
  // paying per case. quotaFilled's own "pay for this case" is unaffected —
  // that is an overage on a real plan, not pay-per-case.
  const continueConfig =
    reason === 'payPerCase' && !SHOW_PAY_PER_CASE ? null : CONTINUE_CONFIG[reason];
  const continueAmount = reason === 'quotaFilled' ? overageAmount : nextCycleAmount;
  const navigate = useNavigate();
  const toast = useToast();
  const [pricing, setPricing] = useState({ merchantPlanPrices: null, payPerCaseTiers: null });
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [subscribeStep, setSubscribeStep] = useState('choose'); // 'choose' | 'plans'
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Kept as a persistent inline error, not a toast: without pricing the plan
  // grid renders empty, and a toast that fades in a few seconds would leave
  // that blank grid with no lasting explanation for why.
  const [loadError, setLoadError] = useState(null);
  const copy = COPY[reason] || COPY.gated;
  // The 'plans' step is much taller than 'choose' — if the modal's own
  // scrollable viewport was scrolled down on 'choose' (a short mobile
  // screen, say), switching steps otherwise keeps that offset and the plan
  // grid can open already scrolled past its own top.
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [subscribeStep]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await api.get('/api/merchants/pricing');
        setPricing(res.data);
      } catch (e) {
        console.error('Fetch pricing error:', e);
        setLoadError('Could not load pricing.');
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const handleSubscribe = () => {
    if (subscribeStep === 'choose') {
      setSubscribeStep('plans');
      return;
    }
    if (!selectedPlan) return;
    if (selectedPlan === 'pilot') {
      doStartPilot();
      return;
    }
    doCheckout('subscribe', selectedPlan);
  };

  /**
   * "Continue as you already are" for payPerCase/quotaFilled: pays for THIS
   * case specifically, via whichever per-case checkout CONTINUE_CONFIG names
   * for the current reason — not activation-checkout, which is for choosing
   * a billing model for the first time.
   */
  const handleContinue = async () => {
    if (!continueConfig) return;
    setActivating(true);
    try {
      const { data } = await api.post(continueConfig.endpoint, { token });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast('Invalid response from server.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Checkout could not be started. Please try again.', {
        tone: 'error'
      });
    } finally {
      setActivating(false);
    }
  };

  const doCheckout = async (choice, plan) => {
    setActivating(true);
    try {
      const { data } = await api.post('/api/stripe/activation-checkout', {
        token,
        choice,
        plan: choice === 'subscribe' ? (plan || 'merchant') : undefined
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      // No URL but success: an existing subscription was switched in place
      // (proration applied instantly by Stripe) rather than sent to Stripe
      // Checkout for a brand-new one — nothing left to redirect to.
      if (data?.success) {
        onActivated?.('Plan updated. You can now take action on this case.');
        return;
      }
      toast('Invalid response from server.', { tone: 'error' });
    } catch (err) {
      toast(err.response?.data?.error || 'Checkout could not be started. Please try again.', {
        tone: 'error'
      });
    } finally {
      setActivating(false);
    }
  };

  /**
   * Pilot is free — nothing to check out, so this bypasses Stripe entirely.
   * A fresh login is created alongside it (unless one already exists), so a
   * pilot started from one case's magic link isn't the only way back in —
   * the returned token sends them straight to set a password.
   */
  const doStartPilot = async () => {
    setActivating(true);
    try {
      const { data } = await api.post(`/api/merchants/case/${token}/start-pilot`);
      if (data?.setPasswordToken) {
        navigate(`/set-password/${data.setPasswordToken}`);
        return;
      }
      onActivated?.('Pilot started. You can now take action on this case.');
    } catch (err) {
      toast(err.response?.data?.error || 'Could not start the pilot. Please try again.', {
        tone: 'error'
      });
    } finally {
      setActivating(false);
    }
  };

  const handleViewPricing = () => {
    if (onViewPricing) {
      onViewPricing();
    } else {
      navigate('/pricing');
    }
  };

  const plans = pricing.merchantPlanPrices || {};
  // Every plan the admin has configured, in the canonical order — the same set
  // the public pricing page shows, so the two never disagree. Pilot and
  // Enterprise are displayed but not purchasable here; the card render marks
  // them, and the server rejects them at checkout regardless.
  const planOrder = PLAN_KEYS.filter((key) => plans[key] != null);

  return (
    <div
      ref={scrollRef}
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brand-950/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
    >
      {/* Widens only for the plan ladder: five cards need the room, but the
          two-option first step would look marooned in it. */}
      <div
        className={`animate-rise max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-panel transition-[max-width] duration-200 sm:rounded-2xl ${
          subscribeStep === 'plans' ? 'max-w-xl' : 'max-w-lg'
        }`}
      >
        <div className="p-6 sm:p-8">
          <h2 className="font-display text-xl font-700 leading-snug tracking-tight text-ink-900">
            {copy.heading}
          </h2>
          <p className="mt-2.5 text-[15px] leading-relaxed text-ink-600">{copy.intro}</p>

          {loadError && (
            <Alert tone="danger" className="mt-5">
              {loadError}
            </Alert>
          )}

          {subscribeStep === 'choose' ? (
            <>
              <div className="mt-6 space-y-3">
                {continueConfig && (
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={activating}
                    className="flex w-full items-start gap-3.5 rounded-xl border-2 border-gold-500 bg-gold-50/40 p-4 text-left transition-colors hover:bg-gold-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 disabled:opacity-50"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold-50 text-gold-700">
                      <IconCard size={19} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-display text-[15px] font-700 text-ink-900">
                        {activating
                          ? 'Redirecting to payment…'
                          : continueAmount != null
                            ? `${continueConfig.label} — £${Number(continueAmount).toFixed(2)}`
                            : continueConfig.label}
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-ink-600">
                        {continueConfig.subtitle}
                      </span>
                    </span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setSubscribeStep('plans')}
                  disabled={activating}
                  className="flex w-full items-start gap-3.5 rounded-xl border-2 border-ink-200 p-4 text-left transition-colors hover:border-brand-700 hover:bg-brand-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 disabled:opacity-50"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                    <IconCheckCircle size={19} />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display text-[15px] font-700 text-ink-900">
                      {canStartPilot
                        ? 'Choose a plan'
                        : continueConfig
                          ? continueConfig.switchLabel
                          : 'Buy a license'}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-ink-600">
                      {copy.subscribeSubtitle}
                    </span>
                  </span>
                </button>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-ink-500">
                Customers are not charged. ParcelBuddy only bills merchants after value is delivered.
              </p>

              <button
                type="button"
                onClick={handleViewPricing}
                className="mt-4 rounded text-sm font-medium text-brand-700 underline transition-colors hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                View pricing details
              </button>

              {loading && <p className="mt-4 text-sm text-ink-500">Loading pricing…</p>}
            </>
          ) : (
            <>
              <p className="mt-6 text-sm font-600 text-ink-800">Choose a plan:</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {planOrder.map((key) => {
                  const pl = plans[key];
                  const price = typeof pl === 'object' && pl.price != null ? pl.price : pl;
                  const includedCases = typeof pl === 'object' ? pl.includedCases : null;
                  const includedUsers = typeof pl === 'object' ? pl.includedUsers : null;
                  const overThresholdFee = typeof pl === 'object' ? pl.overThresholdFee : null;
                  const isCustom = typeof pl === 'object' && Boolean(pl.isCustom);

                  // Quote-only if the admin flagged it OR if the plan simply is
                  // not something you can buy here.
                  const quoteOnly = isCustom || (!isSelfServePlan(key) && key !== 'pilot');

                  const priceLabel = quoteOnly
                    ? 'Custom'
                    : Number(price) === 0
                      ? 'Free'
                      : `£${Number(price).toFixed(2)}`;
                  const priceHint = quoteOnly
                    ? 'Talk to us'
                    : Number(price) === 0
                      ? 'to try it'
                      : '/month';

                  // Every configured plan is shown, but only the self-serve ones
                  // can be bought here — the server rejects the rest at
                  // checkout. Pilot is the one exception: it is never
                  // self-serve-purchasable (it has no price), but it IS
                  // selectable here when this specific merchant is still
                  // eligible for one, routed to /start-pilot instead of Stripe.
                  // The plan already held isn't selectable either — nothing to
                  // buy there, it's just a note, not a re-purchasable option.
                  const isCurrent = currentPlan === key;
                  const selectable =
                    !isCurrent && ((isSelfServePlan(key) && !isCustom) || (key === 'pilot' && canStartPilot));
                  const isSelected = selectedPlan === key;
                  const accent = planAccent(key);

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={selectable ? () => setSelectedPlan(key) : undefined}
                      disabled={!selectable}
                      aria-pressed={selectable ? isSelected : undefined}
                      className={`rounded-xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                        isCurrent
                          ? 'cursor-default border-brand-800 bg-brand-50/60'
                          : !selectable
                            ? 'cursor-default border-ink-200 bg-ink-50/60'
                            : isSelected
                              ? `${accent.border} bg-gradient-to-b ${accent.wash}`
                              : `${accent.borderSoft} hover:border-ink-300`
                      }`}
                    >
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-md font-display text-xs font-800 ${accent.badgeBg} ${accent.badgeText}`}
                      >
                        {planLabel(key).charAt(0)}
                      </span>
                      <p className="mt-2 font-display text-sm font-700 text-ink-900">{planLabel(key)}</p>
                      <p className="mt-1.5 font-display text-lg font-800 tracking-tight text-ink-900">
                        {priceLabel}
                      </p>
                      <p className="text-xs text-ink-500">{priceHint}</p>
                      <p className="mt-1.5 text-xs leading-relaxed text-ink-600">
                        {includedCases != null
                          ? `${includedCases.toLocaleString('en-GB')} cases${
                              includedUsers != null ? `, ${includedUsers} users` : ''
                            } included${
                              overThresholdFee != null && overThresholdFee > 0
                                ? `, then £${Number(overThresholdFee).toFixed(2)} each`
                                : ''
                            }`
                          : 'Unlimited cases'}
                      </p>
                      {Array.isArray(pl?.features) && pl.features.length > 0 && (
                        <ul className="mt-2 space-y-1 border-t border-ink-100 pt-2">
                          {pl.features.map((feature) => (
                            <li
                              key={feature}
                              className="flex items-start gap-1.5 text-[11px] leading-snug text-ink-600"
                            >
                              <IconCheck size={11} className="mt-0.5 shrink-0 text-brand-600" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      {isCurrent ? (
                        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-600 text-brand-700">
                          <IconCheck size={14} />
                          Current plan
                        </p>
                      ) : (
                        !selectable && (
                          <p className="mt-1.5 text-xs font-600 text-brand-700">
                            {/* Keyed off what the plan *is*, not off a stored flag:
                                Enterprise is quote-only whether or not isCustom has
                                been ticked in admin, and this modal only ever opens
                                for merchants past their free case, so a pilot is no
                                longer open to them. */}
                            {key === 'pilot' ? 'New merchants only' : 'Contact us to set up'}
                          </p>
                        )
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  variant={selectedPlan && selectedPlan !== 'pilot' ? 'gold' : 'primary'}
                  onClick={handleSubscribe}
                  disabled={activating || !selectedPlan}
                  loading={activating}
                >
                  {selectedPlan === 'pilot'
                    ? activating
                      ? 'Starting your pilot…'
                      : 'Start free pilot'
                    : activating
                      ? 'Redirecting to payment…'
                      : 'Subscribe & continue'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => { setSubscribeStep('choose'); setSelectedPlan(null); }}
                  disabled={activating}
                >
                  <IconArrowLeft size={16} />
                  Back
                </Button>
                <button
                  type="button"
                  onClick={handleViewPricing}
                  className="rounded text-sm font-medium text-brand-700 underline transition-colors hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                >
                  View pricing details
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivationModal;
